import React, { useEffect, useMemo, useRef } from "react";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { CalendarServiceInstance, addDays, formatTime, instancesForDay, sameDay, serviceColour } from "./calendarUtils";

interface Props {
  weekStart: Date;
  selectedDate: Date;
  instances: CalendarServiceInstance[];
  onSelectDate: (date: Date) => void;
  onOpen: (instance: CalendarServiceInstance) => void;
}

const HOUR_HEIGHT = 48;

export default function CalendarWeekView({ weekStart, selectedDate, instances, onSelectDate, onOpen }: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const days = useMemo(() => Array.from({ length: 7 }, (_, index) => addDays(weekStart, index)), [weekStart]);
  const hours = Array.from({ length: 24 }, (_, index) => index);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 52 + 7.5 * HOUR_HEIGHT;
  }, []);

  return (
    <div className="calendar-week-view">
      <div className="calendar-week-scroll" ref={scrollRef}>
        <div className="calendar-week-header">
          <div />
          {days.map((day) => (
            <button key={day.toISOString()} type="button" className={sameDay(day, selectedDate) ? "is-selected" : ""} onClick={() => onSelectDate(day)} aria-pressed={sameDay(day, selectedDate)} aria-current={sameDay(day, new Date()) ? "date" : undefined}>
              <span>{day.toLocaleDateString("en-GB", { weekday: "short" })}</span><strong>{day.getDate()}</strong>
            </button>
          ))}
        </div>
        <div className="calendar-week-grid" style={{ minHeight: 24 * HOUR_HEIGHT }}>
          <div className="calendar-time-column">
            {hours.map((hour) => <span key={hour} style={{ top: hour * HOUR_HEIGHT }}>{String(hour).padStart(2, "0")}:00</span>)}
          </div>
          {days.map((day) => (
            <div key={day.toISOString()} className={`calendar-week-day${sameDay(day, selectedDate) ? " is-selected" : ""}`} onClick={() => onSelectDate(day)}>
              {hours.map((hour) => <span key={hour} className="calendar-hour-line" style={{ top: hour * HOUR_HEIGHT }} />)}
              {instancesForDay(instances, day).map((instance) => {
                const start = new Date(instance.startDateTime);
                const end = new Date(instance.endDateTime);
                const startMinutes = start.getHours() * 60 + start.getMinutes();
                const durationMinutes = Math.max(30, (end.getTime() - start.getTime()) / 60000);
                return (
                  <button key={instance.serviceInstanceId} type="button" className="calendar-week-event" style={{ top: startMinutes / 60 * HOUR_HEIGHT, height: Math.max(28, durationMinutes / 60 * HOUR_HEIGHT), ["--service-colour" as string]: serviceColour(instance) }} onClick={(event) => { event.stopPropagation(); onSelectDate(day); onOpen(instance); }}>
                    <strong>{instance.serviceName}</strong><span>{formatTime(instance.startDateTime)} – {formatTime(instance.endDateTime)}</span>{instance.venue && <small>{instance.venue}</small>}{(instance.preRegistrationCampaignId || instance.postRegistrationCampaignId) && <MailOutlineIcon className="calendar-campaign-indicator" aria-label="Session communication configured" />}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
